'use client';

import { useState, useRef, useCallback, FormEvent, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NotFoundSearchProps {
    variant?: 'dark' | 'light';
}

/**
 * Extract a readable search term from the last URL segment
 * Converts slugs like "my-page-name" to "my page name"
 */
function extractSearchTermFromPath(pathname: string): string {
    // Get the last segment of the path
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    
    // Convert slug format to readable text (replace hyphens/underscores with spaces)
    return lastSegment
        .replace(/[-_]/g, ' ')
        .trim();
}

export function NotFoundSearch({ variant = 'dark' }: NotFoundSearchProps) {
    const pathname = usePathname();
    const initialQuery = useMemo(() => extractSearchTermFromPath(pathname), [pathname]);
    
    const [query, setQuery] = useState(initialQuery);
    const inputRef = useRef<HTMLInputElement>(null);
    const router = useRouter();

    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const trimmed = query.trim();

            if (!trimmed) {
                // Keep focus in the field if query is empty
                inputRef.current?.focus();
                return;
            }

            router.push(`/search?q=${encodeURIComponent(trimmed)}`);
        },
        [query, router]
    );

    const isLight = variant === 'light';

    return (
        <form onSubmit={handleSubmit} className="relative w-full">
            <label htmlFor="not-found-search" className="sr-only">
                Search the site
            </label>
            <div className="relative">
                <input
                    ref={inputRef}
                    id="not-found-search"
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search the site"
                    className={cn(
                        'w-full h-12 md:h-14 pl-5 pr-14 text-base md:text-lg rounded-xl',
                        'transition-all',
                        isLight
                            ? [
                                'bg-background border-2 border-border',
                                'text-foreground placeholder:text-muted-foreground',
                                'focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring',
                            ]
                            : [
                                'bg-white/10 backdrop-blur-sm border border-white/20',
                                'text-white placeholder:text-white/50',
                                'focus:outline-none focus:ring-2 focus:ring-white/30 focus:border-white/40',
                            ]
                    )}
                    aria-describedby="search-hint"
                />
                <button
                    type="submit"
                    className={cn(
                        'absolute right-2 top-1/2 -translate-y-1/2',
                        'w-8 h-8 md:w-10 md:h-10 flex items-center justify-center',
                        'bg-primary text-primary-foreground rounded-lg',
                        'hover:bg-primary/90',
                        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                        'transition-colors'
                    )}
                    aria-label="Submit search"
                >
                    <Search className="w-4 h-4 md:w-5 md:h-5" />
                </button>
            </div>
            <span id="search-hint" className="sr-only">
                Enter a search term and press Enter or click the search button to search the site
            </span>
        </form>
    );
}
